import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Button, Modal, Table } from "antd";
import axiosInstance from "../api/axiosInstance";
import {
  CardTitle,
  DetailList,
  ListToolbar,
  PageHeader,
  Section,
  SectionCard,
  StatusTag,
  tablePagination,
  useFlatPage,
} from "../components/ui";
import { IconInfo, IconPlus } from "../components/ui/icons";

const AmenitiesPage = () => {
  useFlatPage();
  const navigate = useNavigate();
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [search, setSearch] = useState("");

  const fetchHotels = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get("/Hotel");
      setHotels(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error("Error fetching hotels:", err);
      setError("Failed to load hotels. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHotels();
  }, []);

  const visible = hotels.filter((h) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return `${h.hotelName || ""} ${h.address || ""} ${(h.facilities || []).join(" ")}`.toLowerCase().includes(q);
  });

  const columns = [
    {
      title: "Hotel",
      dataIndex: "hotelName",
      key: "hotelName",
      render: (v, h) => (
        <div className="hms-cell-stack">
          <span className="hms-cell-main">{v}</span>
          <span className="hms-cell-sub">{h.address || "—"}</span>
        </div>
      ),
    },
    { title: "Contact", dataIndex: "contactNumber", key: "contactNumber", render: (v) => v || <span className="hms-muted">—</span> },
    {
      title: "Rating",
      dataIndex: "rating",
      key: "rating",
      width: 100,
      render: (v) => (v ? `${v} / 5` : <span className="hms-muted">—</span>),
    },
    {
      title: "Amenities",
      dataIndex: "facilities",
      key: "facilities",
      render: (list) =>
        Array.isArray(list) && list.length ? (
          <span>
            {list.slice(0, 4).map((f) => (
              <StatusTag key={f} status="confirmed">
                {f}
              </StatusTag>
            ))}
            {list.length > 4 && <span className="hms-pill">+{list.length - 4}</span>}
          </span>
        ) : (
          <span className="hms-muted">None listed</span>
        ),
    },
  ];

  return (
    <div className="hms-page amen-page">
      <PageHeader
        actions={
          <>
            <Button icon={<IconInfo />} onClick={() => setShowInfoModal(true)}>
              Guide
            </Button>
            <Button type="primary" icon={<IconPlus />} onClick={() => navigate("/hotels/add-hotel")}>
              Add hotel
            </Button>
          </>
        }
      />

      {error && (
        <Alert
          style={{ marginBottom: 12 }}
          type="error"
          showIcon
          title={error}
          action={<Button onClick={fetchHotels}>Retry</Button>}
        />
      )}

      <SectionCard
        flush
        title={<CardTitle count={hotels.length}>Hotels and amenities</CardTitle>}
        extra={
          <ListToolbar
            search={{ value: search, onChange: setSearch, placeholder: "Search hotel, address or amenity" }}
            onRefresh={fetchHotels}
            loading={loading}
          />
        }
      >
        <Table
          className="hms-table"
          columns={columns}
          dataSource={visible}
          loading={loading}
          rowKey={(h) => h.hotelId}
          pagination={tablePagination("hotel")}
          scroll={{ x: 800 }}
          locale={{
            emptyText: (
              <div className="hms-empty">
                No hotels found.{" "}
                <Button type="link" onClick={() => navigate("/hotels/add-hotel")}>
                  Add a hotel
                </Button>
              </div>
            ),
          }}
        />
      </SectionCard>

      <Modal
        title="Hotels and amenities guide"
        open={showInfoModal}
        onCancel={() => setShowInfoModal(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setShowInfoModal(false)}>
            Got it
          </Button>,
        ]}
        width={520}
        destroyOnHidden
      >
        <p className="hms-modal-note">All hotels in the system and the amenities each one offers.</p>
        <Section title="What you can do">
          <ul style={{ paddingLeft: 20, margin: 0 }}>
            <li>See contact details and ratings for each property</li>
            <li>Search by hotel name, address or amenity</li>
            <li>Add a new hotel</li>
          </ul>
        </Section>
        <Section title="Right now">
          <DetailList items={[{ label: "Total hotels", value: hotels.length }]} />
        </Section>
      </Modal>
    </div>
  );
};

export default AmenitiesPage;
